import { Heading, Paragraph } from '@zillow/constellation-10';
import { formatDate } from '@zg-rentals/formatters';
import { InlineDd, InlineDt } from '../../BackgroundReportEvictions/EvictionSectionContent';
import { getTitleCase } from '../../../../utils/formatters';
import { CriminalOffense } from './CriminalOffense';
import type { CriminalRecord } from '@zg-rentals/ts-capps-client';

export function CriminalSectionContent({ record }: { record: CriminalRecord }) {
  const { subject, offenses } = record;
  const {
    fullName,
    alias,
    dob,
    sex,
    hairColor,
    eyeColor,
    weight,
    height,
    skinTone,
    militaryService,
    age,
    ageAtDeath,
    deathVerificationCode,
    scarsMarks,
    address,
    caseNumber,
    state,
    category,
    sourceOfRecord,
    jurisdiction,
    comments,
  } = subject;

  return (
    <>
      {(fullName ||
        alias ||
        dob ||
        sex ||
        hairColor ||
        eyeColor ||
        weight ||
        height ||
        skinTone ||
        militaryService ||
        age ||
        ageAtDeath ||
        deathVerificationCode ||
        scarsMarks ||
        address) && (
        <>
          <Heading level={5} css={{ textStyle: 'body-xs-bold', color: 'text.subtle', marginBottom: 'tighter' }}>
            Subject information
          </Heading>
          <dl>
            {fullName && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Full name:</InlineDt> <InlineDd>{fullName}</InlineDd>
              </Paragraph>
            )}
            {alias && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Alias:</InlineDt> <InlineDd>{alias}</InlineDd>
              </Paragraph>
            )}
            {dob && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Date of birth:</InlineDt> <InlineDd>{formatDate(dob, 'numbers')}</InlineDd>
              </Paragraph>
            )}
            {sex && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Sex:</InlineDt> <InlineDd>{getTitleCase(sex)}</InlineDd>
              </Paragraph>
            )}
            {hairColor && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Hair color:</InlineDt> <InlineDd>{getTitleCase(hairColor)}</InlineDd>
              </Paragraph>
            )}
            {eyeColor && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Eye color:</InlineDt> <InlineDd>{getTitleCase(eyeColor)}</InlineDd>
              </Paragraph>
            )}
            {weight && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Weight:</InlineDt> <InlineDd>{weight.toLowerCase()}</InlineDd>
              </Paragraph>
            )}
            {height && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Height:</InlineDt> <InlineDd>{height.toLowerCase()}</InlineDd>
              </Paragraph>
            )}
            {skinTone && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Skin tone:</InlineDt> <InlineDd>{getTitleCase(skinTone)}</InlineDd>
              </Paragraph>
            )}
            {militaryService && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Military service:</InlineDt> <InlineDd>{getTitleCase(militaryService)}</InlineDd>
              </Paragraph>
            )}
            {age && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Age:</InlineDt> <InlineDd>{age}</InlineDd>
              </Paragraph>
            )}
            {ageAtDeath && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Age at death:</InlineDt> <InlineDd>{ageAtDeath}</InlineDd>
              </Paragraph>
            )}
            {deathVerificationCode && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Death verification code:</InlineDt> <InlineDd>{deathVerificationCode}</InlineDd>
              </Paragraph>
            )}
            {scarsMarks && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Scars/Marks:</InlineDt> <InlineDd>{getTitleCase(scarsMarks)}</InlineDd>
              </Paragraph>
            )}
            {address && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Address:</InlineDt> <InlineDd>{address}</InlineDd>
              </Paragraph>
            )}
          </dl>

          {(caseNumber || state || category || sourceOfRecord || jurisdiction || comments) && (
            <>
              <Heading
                level={5}
                css={{ textStyle: 'body-xs-bold', color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}
              >
                Case details
              </Heading>
              {caseNumber && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>Case number:</InlineDt> <InlineDd>{caseNumber}</InlineDd>
                </Paragraph>
              )}
              {state && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>State:</InlineDt> <InlineDd>{state}</InlineDd>
                </Paragraph>
              )}
              {category && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>Category:</InlineDt> <InlineDd>{getTitleCase(category)}</InlineDd>
                </Paragraph>
              )}
              {sourceOfRecord && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>Source:</InlineDt> <InlineDd>{sourceOfRecord}</InlineDd>
                </Paragraph>
              )}
              {jurisdiction && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>Jurisdiction:</InlineDt> <InlineDd>{jurisdiction}</InlineDd>
                </Paragraph>
              )}
              {comments && (
                <Paragraph textStyle="body-sm">
                  <InlineDt>Comments:</InlineDt> <InlineDd>{comments}</InlineDd>
                </Paragraph>
              )}
            </>
          )}

          {offenses.map((offense, index) => (
            <CriminalOffense offense={offense} key={index} />
          ))}
        </>
      )}
    </>
  );
}
